import { usePaneContext } from "@/components/pane/paneContext.ts";
import { useAuth } from "@/hooks/useAuth.ts";
import {
  ICascadeLevelDetails,
  useCascadeLevelsBySymbol,
} from "@/hooks/useCascadeLevelsBySymbol.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import {
  IMarginLevelDetails,
  useMarginLevelsBySymbol,
} from "@/hooks/useMarginLevelsBySymbol.ts";
import { BINANCE_RESOLUTION_MAP } from "@/lib/datafeed/config.ts";
import { datafeed } from "@/lib/datafeed/df.ts";

// From here the indicators are imported into the Chart Pane

import { useDashboard } from "@/lib/store.ts";
import {
  ChartingLibraryFeatureset,
  ChartingLibraryWidgetOptions,
  CustomIndicator,
  EntityId,
  IChartingLibraryWidget,
  IDropdownApi,
  IOrderLineAdapter,
  PineJS,
  ResolutionString,
  SaveLoadChartRecord,
  StudyInputValue,
  widget as Widget,
} from "@tradingView/charting_library";
import { merge } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrawSignal } from "@/lib/store";
import { useMarketplaceStore } from "@/lib/stores/useMarketplaceStore";
import { useAlgosStore } from "@/lib/stores/algosStore";
import { useUser } from "@/hooks/useUser";
import { getSFIndicators } from "./indicators-access";
import { TradesBubbles } from "@/components/shared/TradesBubbles";
import { Input } from "@/components/ui/input";
import { options } from "numeral";
import { toast } from "@/components/ui/use-toast";
import FloatingTradeWidget from "@/components/core/charts/FloatingTradeWidget";
import { MANUAL_COLOR } from "@/domain/constants";
import { saveLoadDomain } from "@/lib/constants";

export const SfChartFullFunctional = memo(function SfChartFullFunctional() {
  const nodeId = usePaneContext();
  const { updateTabConfig, getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const { user } = useAuth();
  const { user: userDoc } = useUser(user?.uid);

  const { colorsToSymbolMap, setSymbolForColor } = useDashboard((state) => ({
    colorsToSymbolMap: state.colorsToSymbolMap,
    setSymbolForColor: state.setSymbolForColor,
  }));

  const nodeSymbol = colorsToSymbolMap[config.color] || config.symbol;
  const { purchasedProductIds } = useMarketplaceStore();

  const selectedAlgo = useAlgosStore((s) => s.selectedAlgo);

  const cascadeLevels = useCascadeLevelsBySymbol(
    BINANCE_RESOLUTION_MAP[config.interval] || "5m",
    nodeSymbol,
  );

  const marginLevels = useMarginLevelsBySymbol(config.symbol, "chartComponent");

  const id = useMemo(
    () => `chartContainer_${Math.random().toString(36).substring(7)}`,
    [],
  );

  const guestId = useMemo(
    () => `guest-${Math.random().toString(36).substring(2, 10)}`,
    [],
  );

  const initialOptions: ChartingLibraryWidgetOptions = useMemo(
    () => ({
      container: id,
      locale: "en",
      library_path: "static/charting_library/charting_library/",
      charts_storage_url: `https://${saveLoadDomain}`,
      charts_storage_api_version: "1.1",
      datafeed: datafeed,
      symbol: "Binance:BTCUSDT",
      interval: "1" as ResolutionString,
      autosize: true,
      custom_indicators_getter: (
        PineJS: PineJS,
      ): Promise<CustomIndicator[]> => {
        return Promise.resolve(
          getSFIndicators(
            PineJS,
            purchasedProductIds,
            userDoc?.role || "basic",
          ),
        );
      },
      disabled_features: [
        "popup_hints",
        "display_market_status",
        "header_saveload",
      ],
      enabled_features: [
        ...(userDoc?.role === "pro" ||
        userDoc?.role === "expert" ||
        purchasedProductIds.includes("CdgAdfiOhld83llezPr0")
          ? (["seconds_resolution"] as ChartingLibraryFeatureset[])
          : ([] as ChartingLibraryFeatureset[])),
        "study_templates",
        "hide_left_toolbar_by_default",
        "show_spread_operators",
        "hide_main_series_symbol_from_indicator_legend",
        "hide_price_scale_global_last_bar_value",
        "study_overlay_compare_legend_option",
        "use_localstorage_for_settings",
        "two_character_bar_marks_labels",
      ],
      client_id: "sf-front-v2",
      time_scale: {},
      theme: "dark",
      overrides: {
        // Background
        "paneProperties.background": "#0f0f0f",
        "paneProperties.backgroundType": "solid",
        // Candles
        "mainSeriesProperties.barStyle.upColor": "#3CA64B",
        "mainSeriesProperties.barStyle.downColor": "#B2182C",
        "mainSeriesProperties.candleStyle.upColor": "#3CA64B",
        "mainSeriesProperties.candleStyle.downColor": "#B2182C",
        "mainSeriesProperties.candleStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.candleStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.candleStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.candleStyle.wickDownColor": "#B2182C",
        "mainSeriesProperties.hollowCandleStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.hollowCandleStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.hollowCandleStyle.upColor": "#3CA64B",
        "mainSeriesProperties.hollowCandleStyle.downColor": "#B2182C",
        "mainSeriesProperties.hollowCandleStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.hollowCandleStyle.wickDownColor": "#B2182C",
        // Lines
        "mainSeriesProperties.lineStyle.color": "#ffffff",
        "mainSeriesProperties.lineWithMarkersStyle.color": "#ffffff",
        "mainSeriesProperties.steplineStyle.color": "#ffffff",
        // Areas
        "mainSeriesProperties.areaStyle.color1": "rgba(13, 157, 219, 0.25)",
        "mainSeriesProperties.areaStyle.color2": "rgba(13, 157, 219, 0.05)",
        "mainSeriesProperties.areaStyle.linecolor": "#ffffff",
        "mainSeriesProperties.hlcAreaStyle.closeLowFillColor":
          "rgba(75, 192, 91, 0.2)",
        "mainSeriesProperties.hlcAreaStyle.highCloseFillColor":
          "rgba(204, 43, 43, 0.2)",
        "mainSeriesProperties.hlcAreaStyle.highLineColor": "#B2182C",
        "mainSeriesProperties.hlcAreaStyle.lowLineColor": "#3CA64B",
        "mainSeriesProperties.baselineStyle.topFillColor1":
          "rgba(75, 192, 91, 0.25)",
        "mainSeriesProperties.baselineStyle.topFillColor2":
          "rgba(75, 192, 91, 0.05)",
        "mainSeriesProperties.baselineStyle.topLineColor": "#3CA64B",
        "mainSeriesProperties.baselineStyle.bottomFillColor1":
          "rgba(204, 43, 43, 0.05)",
        "mainSeriesProperties.baselineStyle.bottomFillColor2":
          "rgba(204, 43, 43, 0.25)",
        "mainSeriesProperties.baselineStyle.bottomLineColor": "#B2182C",
        // Exotic charts
        "mainSeriesProperties.columnStyle.upColor": "rgba(75, 192, 91, 0.5)",
        "mainSeriesProperties.columnStyle.downColor": "rgba(204, 43, 43, 0.5)",
        "mainSeriesProperties.hiloStyle.borderColor": "#3CA64B",
        "mainSeriesProperties.hiloStyle.color": "#3CA64B",
        "mainSeriesProperties.hiloStyle.labelColor": "#3CA64B",
        "mainSeriesProperties.haStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.haStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.haStyle.upColor": "#3CA64B",
        "mainSeriesProperties.haStyle.downColor": "#B2182C",
        "mainSeriesProperties.haStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.haStyle.wickDownColor": "#B2182C",
        "mainSeriesProperties.kagiStyle.upColor": "#3CA64B",
        "mainSeriesProperties.kagiStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.kagiStyle.downColor": "#B2182C",
        "mainSeriesProperties.kagiStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.pbStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.pbStyle.borderUpColorProjection": "#3CA64B",
        "mainSeriesProperties.pbStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.pbStyle.borderDownColorProjection": "#B2182C",
        "mainSeriesProperties.pbStyle.upColor": "#3CA64B",
        "mainSeriesProperties.pbStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.pbStyle.downColor": "#B2182C",
        "mainSeriesProperties.pbStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.pnfStyle.upColor": "#3CA64B",
        "mainSeriesProperties.pnfStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.pnfStyle.downColor": "#B2182C",
        "mainSeriesProperties.pnfStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.renkoStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.renkoStyle.borderUpColorProjection": "#3CA64B",
        "mainSeriesProperties.renkoStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.renkoStyle.borderDownColorProjection": "#B2182C",
        "mainSeriesProperties.renkoStyle.upColor": "#3CA64B",
        "mainSeriesProperties.renkoStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.renkoStyle.downColor": "#B2182C",
        "mainSeriesProperties.renkoStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.renkoStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.renkoStyle.wickDownColor": "#B2182C",
      },
      studies_overrides: {
        "volume.volume.color.0": "rgba(178, 24, 44, 0.5)",
        "volume.volume.color.1": "rgba(60, 166, 75, 0.5)",
        "volume.volume ma:plot.color": "rgb(255, 255, 255)",
        "volume.volume ma:plot.linewidth": 1,
        "volume.volume ma:plot.display": 1,
        // 'volume profile visible range.histBars2.color.0': 'rgba(178, 24, 44, 0.5)',
        // 'volume profile visible range.histBarsVA.color.1': 'rgba(60, 166, 75, 0.5)',
        // 'volume profile visible range.pocLines.color': 'rgb(255, 255, 255)',
        "volume profile visible range.developing poc.color":
          "rgb(255, 255, 255)",
        "volume profile visible range.developing va high.color":
          "rgb(255, 255, 255)",
        "volume profile visible range.developing va high.linestyle": 2,
        "volume profile visible range.developing va low.color":
          "rgb(255, 255, 255)",
        "volume profile visible range.developing va low.linestyle": 2,
      },
      user_id: user?.uid || guestId,
      custom_css_url: "/chart.css",
      debug: false,
    }),
    [id, user?.uid, guestId, purchasedProductIds, userDoc?.role],
  );

  const widgetRef = useRef<IChartingLibraryWidget | null>(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  // Track if we're currently in an interval change transition
  const isIntervalChangingRef = useRef(false);

  // useEffect(() => {
  //   console.log("purchasedProductIds", purchasedProductIds);
  // }, [purchasedProductIds]);

  useEffect(() => {
    if (isWidgetReady && widgetRef.current) {
      widgetRef.current
        .chart()
        .onIntervalChanged()
        .subscribe(null, (interval) => {
          // Set flag to indicate we're in a transition
          isIntervalChangingRef.current = true;
          console.log("Interval changing started");

          updateTabConfig({ interval }, nodeId);

          // Clear existing levels when interval changes to prevent script engine stops
          clearCascadeLevels();
          // clearMarginLevels();

          // Reset the flag after a delay to allow chart to stabilize
          setTimeout(() => {
            isIntervalChangingRef.current = false;
            console.log(
              "Interval change completed, chart should be stable now",
            );
          }, 1000);
        });
    } else {
      console.debug("Widget not ready");
    }
  }, [isWidgetReady, nodeId, updateTabConfig]);

  const getSaveChartDetails = async (
    id: string,
  ): Promise<SaveLoadChartRecord | undefined> => {
    return new Promise((r) => {
      widgetRef.current?.getSavedCharts((e: SaveLoadChartRecord[]) => {
        const chart = e.find((e) => e.name === id);
        r(chart);
      });
    });
  };

  useEffect(() => {
    if (isWidgetReady && widgetRef.current) {
      widgetRef.current
        .activeChart()
        .onSymbolChanged()
        .subscribe(null, () => {
          updateTabConfig(
            { symbol: widgetRef.current?.chart().symbol() },
            nodeId,
          );
          setSymbolForColor(
            config.color,
            widgetRef.current?.chart().symbol() || nodeSymbol,
          );
        });
    }
  }, [
    isWidgetReady,
    nodeSymbol,
    updateTabConfig,
    setSymbolForColor,
    config,
    nodeId,
  ]);

  // Initial widget creation
  useEffect(() => {
    // If widget exists but userDoc.role changed, remove it first
    if (widgetRef.current) {
      // console.log("Recreating widget due to role change:", userDoc?.role);
      setIsWidgetReady(false);
      widgetRef.current.remove();
      widgetRef.current = null;
    }

    const newWidget = new Widget(merge({}, initialOptions, options));
    widgetRef.current = newWidget;

    newWidget.onChartReady(() => {
      setIsWidgetReady(true);
      getSaveChartDetails(nodeId).then((chartFromServer) => {
        if (chartFromServer && widgetRef.current) {
          widgetRef.current.loadChartFromServer(chartFromServer);
        }
      });
    });

    return () => {
      if (widgetRef.current) {
        setIsWidgetReady(false);
        widgetRef.current.remove();
        widgetRef.current = null;
      }
    };
  }, [initialOptions, nodeId, userDoc?.role]);

  useEffect(() => {
    if (!widgetRef.current || !isWidgetReady) {
      return;
    }
    widgetRef.current?.chart().setSymbol(nodeSymbol);
  }, [isWidgetReady, nodeSymbol]);

  useEffect(() => {
    if (widgetRef.current && isWidgetReady) {
      widgetRef.current.subscribe("onAutoSaveNeeded", () => {
        widgetRef.current?.saveChartToServer(
          () => {},
          () => {},
          { chartName: nodeId },
        );
      });
    }
  }, [nodeId, isWidgetReady]);

  const cascadeLevelsRef = useRef<ICascadeLevelDetails[] | null>(null);
  const marginLevelsRef = useRef<IMarginLevelDetails[] | null>(null);
  const marginLevelsEntitiesRef = useRef<EntityId[] | null>(null);
  const cascadeLevelsEntitiesRef = useRef<EntityId[] | null>(null);

  useEffect(() => {
    if (cascadeLevels.data) {
      cascadeLevelsRef.current = cascadeLevels.data;
    }
    if (marginLevels.data) {
      marginLevelsRef.current = marginLevels.data;
    }
  }, [cascadeLevels, marginLevels]);

  const drawCascadeLevels = useCallback(
    (interval: ResolutionString) => {
      if (!cascadeLevelsRef.current) {
        return;
      }

      // Only proceed if widget is ready
      if (!widgetRef.current || !isWidgetReady) {
        return;
      }

      if (interval === "1") {
        toast({
          title: "Cascade levels error",
          description: "Cascade levels are not available for 1 minute interval",
          variant: "destructive",
        });
        return;
      }
      // Clear previous cascade levels
      if (cascadeLevelsEntitiesRef.current) {
        cascadeLevelsEntitiesRef.current.forEach((shape) => {
          try {
            widgetRef.current?.chart().removeEntity(shape);
          } catch (error) {
            console.error("Error removing cascade level entity:", error);
          }
        });
      }

      cascadeLevelsEntitiesRef.current = [];

      try {
        if (cascadeLevelsRef.current?.length === 0) {
          return;
        }
        cascadeLevelsRef.current?.forEach((level) => {
          if (cascadeLevelsEntitiesRef.current && widgetRef.current) {
            const shape = widgetRef.current
              .chart()
              .createMultipointShape(
                [{ time: level.timestamp / 1000, price: level.price }],
                {
                  shape: "horizontal_ray",
                  text: level.logicFc
                    ? `⇛ ${level.fcSum.toString()}`
                    : level.fcSum.toString(),
                  lock: false,
                  overrides: {
                    fontsize: level.logicFc ? 20 : 12,
                    linewidth: 2,
                    linecolor: level.side === "UP" ? "#B2182C" : "#3CA64B",
                    linestyle: 1,
                    showLabel: true,
                    textcolor: "#ffffff",
                    bold: false,
                    vertLabelsAlign: "bottom",
                    horzLabelsAlign: "right",
                    showPrice: true,
                  },
                },
              );
            if (shape) {
              cascadeLevelsEntitiesRef.current.push(shape);
            }
          }
        });
      } catch (error) {
        console.error("Error drawing cascade levels:", error);
      }
    },
    [isWidgetReady],
  );

  // Function to safely check if chart is ready for drawing
  const isChartReadyForDrawing = useCallback(() => {
    if (!widgetRef.current || !isWidgetReady) {
      return false;
    }
    if (isIntervalChangingRef.current) {
      return false;
    }

    try {
      const chart = widgetRef.current.chart();
      return chart && typeof chart.createMultipointShape === "function";
    } catch (e) {
      console.error("Error checking chart readiness:", e);
      return false;
    }
  }, [isWidgetReady]);

  const drawMarginLevels = useCallback(() => {
    if (!marginLevelsRef.current) {
      console.log("No margin levels data to draw");
      return;
    }

    // If we're in the middle of an interval change, delay the drawing
    if (isIntervalChangingRef.current) {
      clearMarginLevels();
      console.log("Interval is changing, delaying margin level drawing");
      setTimeout(() => drawMarginLevels(), 1200);
      return;
    }

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    clearMarginLevels();

    // Only proceed if widget is ready
    if (!isChartReadyForDrawing()) {
      console.log("Chart not ready for drawing margin levels, will retry");
      // Retry after a delay
      setTimeout(() => {
        if (isChartReadyForDrawing()) {
          clearMarginLevels();
          console.log("Retrying margin level drawing");
          drawMarginLevels();
        } else {
          console.log("Chart still not ready after retry, giving up");
        }
      }, 1000);
      return;
    }

    // Ensure marginLevelsEntitiesRef is initialized
    if (!marginLevelsEntitiesRef.current) {
      marginLevelsEntitiesRef.current = [];
    }

    try {
      const chart = widgetRef.current!.chart();
      clearMarginLevels();

      // Find the max quoteQuantity for BUY and SELL separately
      const buyLevels =
        marginLevelsRef.current?.filter((level) => level.side === "UP") ?? [];
      const sellLevels =
        marginLevelsRef.current?.filter((level) => level.side === "DOWN") ?? [];
      const maxBuyQuoteQuantity = Math.max(
        ...(buyLevels.map((level) => level.quoteQuantity) ?? [0]),
      );
      const maxSellQuoteQuantity = Math.max(
        ...(sellLevels.map((level) => level.quoteQuantity) ?? [0]),
      );

      marginLevelsRef.current?.forEach((level) => {
        try {
          // Determine if this is the max buy or max sell level
          const isMaxBuy =
            level.side === "UP" &&
            level.quoteQuantity === maxBuyQuoteQuantity &&
            buyLevels.length > 0;
          const isMaxSell =
            level.side === "DOWN" &&
            level.quoteQuantity === maxSellQuoteQuantity &&
            sellLevels.length > 0;

          // Color logic using switch statement
          let linecolor: string;
          switch (true) {
            case isMaxBuy:
            case isMaxSell:
              linecolor = "#FFD600"; // bright yellow for max buy
              break;
            case level.side === "UP":
              linecolor = "#3CA64B";
              break;
            default:
              linecolor = "#B2182C";
          }

          // Create the shape with error handling
          const shape = chart.createMultipointShape(
            [{ time: level.timestamp / 1000, price: level.price }],
            {
              shape: "horizontal_ray",
              lock: false,
              text: formatter.format(level.quoteQuantity),
              overrides: {
                fontsize: 12,
                linewidth: 2,
                linecolor,
                linestyle: 0,
                showLabel: true,
                textcolor: "#ffffff",
                bold: false,
                vertLabelsAlign: "bottom",
                horzLabelsAlign: "right",
                showPrice: true,
              },
            },
          );

          if (shape && marginLevelsEntitiesRef.current) {
            marginLevelsEntitiesRef.current.push(shape);
          } else {
            console.error("Error creating margin level shape - shape is null");
          }
        } catch (shapeError) {
          console.error("Error creating shape:", shapeError);
        }
      });
    } catch (error) {
      console.error("Error drawing margin levels:", error);
    }
  }, [marginLevelsRef, marginLevelsEntitiesRef, isChartReadyForDrawing]);

  useEffect(() => {
    let dropdownApi: Promise<IDropdownApi> | undefined;
    if (widgetRef.current && isWidgetReady) {
      widgetRef.current.headerReady().then(async () => {
        dropdownApi = widgetRef.current?.createDropdown({
          title: "Custom Indicators",
          items: [
            {
              title: "Cascade Levels",
              onSelect: () => {
                console.log("Cascade Levels", config.interval);
                drawCascadeLevels(config.interval);
              },
            },
            {
              title: "Predict Liquidations",
              onSelect: () => {
                // Add a small delay before drawing to ensure chart is ready
                setTimeout(() => drawMarginLevels(), 100);
              },
            },
          ],
        });
      });

      return () => {
        // Cleanup logic if necessary
        dropdownApi?.then((api) => {
          api.remove();
        });
      };
    }
  }, [drawCascadeLevels, drawMarginLevels, isWidgetReady, config]);

  const { signalToDraw, clearSignal } = useDrawSignal();
  const orderLinesRef = useRef<{ OrderID: number; entity: EntityId }[]>([]);

  useEffect(() => {
    async function drawOrderLines() {
      // Clean up old lines
      const currentOrderIds = new Set(
        selectedAlgo?.orders?.map((o) => o.OrderID) ?? [],
      );

      orderLinesRef.current = orderLinesRef.current.filter((lineObj) => {
        if (
          !currentOrderIds.has(lineObj.OrderID) &&
          widgetRef.current &&
          isWidgetReady
        ) {
          try {
            widgetRef.current.activeChart().removeEntity(lineObj.entity);
          } catch (e) {
            console.error("Failed to remove order line", e);
          }
          return false; // Remove from ref
        }
        return true; // Keep in ref
      });

      if (
        !selectedAlgo ||
        !selectedAlgo.orders ||
        !widgetRef.current ||
        !isWidgetReady
      ) {
        return;
      }
      for (const order of selectedAlgo.orders) {
        const existingLine = orderLinesRef.current.find(
          (lineObj) => lineObj.OrderID === order.OrderID,
        );

        if (existingLine && existingLine.entity) {
          continue; // Skip if already drawn and entity is not null
        }

        try {
          const shape = await widgetRef.current
            .activeChart()
            .createMultipointShape(
              [
                {
                  price: Number(order.Price),
                  time: Math.floor(order.CreatedTime / 1000),
                },
              ],
              {
                shape: "horizontal_ray",
                text: `${order.Side}: ${order.Price}`,
                lock: false,
                overrides: {
                  fontsize: 12,
                  linewidth: 2,
                  linecolor: order.Side === "BUY" ? "#3CA64B" : "#B2182C",
                  linestyle: 0,
                  showLabel: true,
                  textcolor: "#ffffff",
                  bold: false,
                  vertLabelsAlign: "bottom",
                  horzLabelsAlign: "right",
                  showPrice: true,
                },
              },
            );
          orderLinesRef.current.push({
            OrderID: order.OrderID,
            entity: shape!,
          });
        } catch (e) {
          console.error("Failed to create order line", e);
        }
      }
    }

    setTimeout(() => {
      drawOrderLines();
    }, 1000);
  }, [selectedAlgo, isWidgetReady]);

  useEffect(() => {
    if (!widgetRef.current || !isWidgetReady) {
      return;
    }
    const len = widgetRef.current?.activeChart().getAllShapes().length;
    if (len == 0) {
      orderLinesRef.current = [];
    }
  }, [selectedAlgo, isWidgetReady]);

  useEffect(() => {
    return () => {
      if (orderLinesRef.current.length && widgetRef.current && isWidgetReady) {
        orderLinesRef.current.forEach((line) => {
          try {
            widgetRef.current?.activeChart().removeEntity(line.entity);
          } catch (e) {
            console.error("Failed to remove order line", e);
          }
        });
        orderLinesRef.current = [];
      }
    };
  }, [isWidgetReady]);

  function clearMarginLevels() {
    // If we're in the middle of an interval change, just reset the array
    // if (isIntervalChangingRef.current) {
    //   console.log("Interval changing, just resetting margin levels array");
    //   marginLevelsEntitiesRef.current = [];
    //   return;
    // }

    if (
      marginLevelsEntitiesRef.current &&
      marginLevelsEntitiesRef.current.length > 0
    ) {
      console.log(
        "Clearing margin levels, count:",
        marginLevelsEntitiesRef.current.length,
      );
      marginLevelsEntitiesRef.current.forEach((shape) => {
        try {
          widgetRef.current?.chart().removeEntity(shape);
        } catch (error) {
          console.error("Error clearing margin level:", error);
        }
      });
      marginLevelsEntitiesRef.current = [];
    } else {
      marginLevelsEntitiesRef.current = [];
    }
  }

  function clearCascadeLevels() {
    if (cascadeLevelsEntitiesRef.current) {
      cascadeLevelsEntitiesRef.current.forEach((shape) => {
        try {
          widgetRef.current?.chart().removeEntity(shape);
        } catch (error) {
          console.error("Error clearing cascade level:", error);
        }
      });
      cascadeLevelsEntitiesRef.current = [];
    }
  }

  const addIndicator = useCallback(
    (
      indicator: string | null,
      color: string | null,
      inputs?: Record<string, StudyInputValue>,
    ) => {
      if (!indicator || !color) {
        return;
      }

      if (color != config.color) {
        return;
      }

      widgetRef.current
        ?.chart()
        .createStudy(indicator, false, false, inputs)
        .then();
    },
    [config],
  );

  // Listen for signals
  useEffect(() => {
    if (!signalToDraw.signal || !signalToDraw.color || !isWidgetReady) {
      return;
    }

    if (config.color == MANUAL_COLOR) {
      toast({
        title: "Manual color",
        description: "Please select a color in chart to sync",
        variant: "destructive",
      });
      return;
    }

    if (signalToDraw && widgetRef.current) {
      if (signalToDraw.signal === "Formations") {
        console.log("Drawing formations", signalToDraw.inputs);
        return;
      }
      if (signalToDraw.signal === "Predict Liquidations") {
        clearMarginLevels();
        // Add a small delay before drawing to ensure chart is ready
        setTimeout(() => drawMarginLevels(), 100);
        return;
      }

      if (signalToDraw.signal === "Cascade Levels") {
        console.log("Drawing cascade levels");
        clearCascadeLevels();
        const resolutionInterval = signalToDraw.inputs
          ?.interval as ResolutionString;
        if (resolutionInterval) {
          setInterval(resolutionInterval);
          widgetRef.current
            .chart()
            .setResolution(resolutionInterval)
            .then(() => {
              setTimeout(() => {
                updateTabConfig({ interval: resolutionInterval }, nodeId);
                setTimeout(() => {
                  drawCascadeLevels(resolutionInterval);
                }, 1000);
              }, 100);
            });
        } else {
          drawCascadeLevels(config.interval);
        }
        return;
      }

      addIndicator(signalToDraw.signal, signalToDraw.color);
      clearSignal(); // Clear the signal after drawing
    }
  }, [
    nodeId,
    updateTabConfig,
    config,
    addIndicator,
    signalToDraw,
    clearSignal,
    isWidgetReady,
    drawMarginLevels,
    drawCascadeLevels,
  ]);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [widgetPosition, setWidgetPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Add resize observer to handle container resizing
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      // Get current container dimensions
      const containerRect = container.getBoundingClientRect();
      const widgetWidth = 160;
      const widgetHeight = 50;
      const maxX = containerRect.width - widgetWidth;
      const maxY = containerRect.height - widgetHeight;

      // Adjust position if needed to keep within bounds
      setWidgetPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest("button")) {
      return; // Don't drag if clicking on buttons
    }
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) {
      return;
    }

    const container = chartContainerRef.current;
    if (!container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const newX = e.clientX - containerRect.left - dragOffset.x;
    const newY = e.clientY - containerRect.top - dragOffset.y;

    // Keep widget within container bounds
    const widgetWidth = 160; // New width for the compact design
    const widgetHeight = 50; // New height for the compact design
    const maxX = containerRect.width - widgetWidth;
    const maxY = containerRect.height - widgetHeight;

    setWidgetPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="w-full h-full relative">
      <div
        ref={chartContainerRef}
        className="relative w-full h-full"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {isDragging && (
          <div
            className="absolute inset-0 z-50"
            style={{ backgroundColor: "transparent" }}
          />
        )}
        {config?.enableQuickTrading && (
          <FloatingTradeWidget
            onBuy={(volume) => {
              // TODO: Implement buy logic
              alert(`Buy ${volume}`);
            }}
            onSell={(volume) => {
              // TODO: Implement sell logic
              alert(`Sell ${volume}`);
            }}
            style={{
              position: "absolute",
              left: widgetPosition.x,
              top: widgetPosition.y,
              zIndex: 1000,
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: "none",
              pointerEvents: "auto",
            }}
            onMouseDown={handleMouseDown}
            symbol={config.symbol}
          />
        )}
        <div id={id} className="w-full h-full absolute inset-0" />
      </div>
    </div>
  );
});
